import { useCallback, useEffect, useState } from 'react'
import { adDetectionApi } from '../../api'
import { ErrorState, errorMessage } from '../../components/ErrorState'
import type { AdDetectionStats, DetectedAdvertiserRow } from '../../types'
import { useShell } from '../../shell/ShellContext'
import { navigate, withQuery } from '../../shell/router'
import { BarList, Block, Kpi, PageHeader, usePercent } from '../../ui/ui'
import { enumLabel, useI18n } from '../../i18n'
import type { TFunction } from '../../i18n'
import { useAds } from './AdsContext'
import { categoryLabel, FiscalPill, platformLabel, QUERY_CATEGORIES, SENSITIVE_CATEGORIES, hostname } from './adsShared'
import { AdsAdvertisers } from './AdsAdvertisers'
import { AdsBanners } from './AdsBanners'
import { AdsCampaigns, ScanStatusBanner } from './AdsCampaigns'
import { AdsQueries } from './AdsQueries'
import './ads.css'

export function AdsModule({ view }: { view: string }) {
  switch (view) {
    case 'advertisers':
      return <AdsAdvertisers />
    case 'banners':
      return <AdsBanners />
    case 'campaigns':
      return <AdsCampaigns />
    case 'queries':
      return <AdsQueries />
    default:
      return <AdsOverview />
  }
}

export function countryName(t: TFunction, code: string): string {
  return enumLabel(t, 'country', code, code)
}

/**
 * Pays au nom masculin : « au Sénégal », mais « en Côte d'Ivoire ».
 *
 * Le genre est propre au français ; l'anglais rend les deux clés par « in ».
 */
const MASCULINE_COUNTRIES = new Set(['SN', 'CM', 'ML', 'BF', 'NE', 'TG', 'BJ', 'GH', 'GA', 'CA'])

export function inCountry(t: TFunction, code: string): string {
  const key = MASCULINE_COUNTRIES.has(code) ? 'ads.inCountryM' : 'ads.inCountryF'
  return t(key, { country: countryName(t, code) })
}

function AdsOverview() {
  const { t, fmt } = useI18n()
  const percent = usePercent()
  const { country } = useShell()
  const { config, refreshKey } = useAds()
  const [stats, setStats] = useState<AdDetectionStats | null>(null)
  const [fresh, setFresh] = useState<{ rows: DetectedAdvertiserRow[]; total: number } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    setError(null)
    Promise.all([
      adDetectionApi.getStats(country || undefined),
      adDetectionApi.getAdvertisers({
        country: country || undefined,
        verificationStatus: 'NEW,TO_REVIEW',
        sortBy: 'lastSeenAt',
        sortOrder: 'desc',
        limit: 6,
      }),
    ])
      .then(([s, list]) => {
        setStats(s)
        setFresh({ rows: list.data, total: list.total })
      })
      .catch((err) => setError(errorMessage(err)))
  }, [country])

  useEffect(load, [load, refreshKey])

  const where = country ? inCountry(t, country) : t('ads.inScope')
  const toReview = (stats?.byVerification?.NEW ?? 0) + (stats?.byVerification?.TO_REVIEW ?? 0)
  const notRegistered = stats?.byFiscalStatus?.NOT_REGISTERED ?? 0
  const sensitive = SENSITIVE_CATEGORIES.reduce((n, c) => n + (stats?.byCategory?.[c] ?? 0), 0)

  const openList = (params: Record<string, string>) => navigate(withQuery('ads/advertisers', params))

  return (
    <>
      <PageHeader
        title={t('ads.overviewTitle', { where })}
        description={
          stats?.lastScanAt
            ? t('ads.overviewDescScanned', { date: fmt.dateTime(stats.lastScanAt) })
            : t('ads.overviewDesc')
        }
        actions={
          <button type="button" className="btn btn-primary" onClick={() => navigate('ads/campaigns')}>
            {t('ads.runScan')}
          </button>
        }
      />

      <ScanStatusBanner />

      {/*
        La collecte Meta passe par Apify, pas par l'Ad Library : l'API Graph répond
        « API access blocked » et n'est plus interrogée. Nommer l'Ad Library ici
        enverrait chercher une clé Meta qui ne servirait à rien.
      */}
      {config && (!config.metaAdsApifyConfigured || !config.googleAdsConfigured) && (
        <p className="ads-config-note" role="note">
          {!config.metaAdsApifyConfigured && !config.googleAdsConfigured
            ? t('ads.noSourceConfigured')
            : !config.metaAdsApifyConfigured
              ? t('ads.noMetaConfigured')
              : t('ads.noGoogleConfigured')}
        </p>
      )}

      {error ? (
        <ErrorState message={error} onRetry={load} />
      ) : !stats ? (
        <div className="stat-skeleton" style={{ minHeight: 320 }} />
      ) : (
        <>
          <div className="grid-kpi">
            <Kpi
              label={t('ads.kpiAdvertisers')}
              value={fmt.number(stats.sponsoredAdvertisers)}
              sub={t('ads.kpiAdvertisersSub', { n: stats.newLast30Days, organic: stats.organicResults })}
              onClick={() => openList({ type: 'SPONSORED' })}
            />
            <Kpi label={t('ads.kpiToReview')} value={toReview} sub={t('ads.kpiToReviewSub')} tone={toReview > 0 ? 'warn' : undefined} onClick={() => openList({ view: 'review' })} />
            <Kpi
              label={t('ads.kpiUnregistered')}
              value={notRegistered}
              sub={t('ads.kpiUnregisteredSub', { percent: percent(notRegistered, stats.totalAdvertisers) })}
              tone={notRegistered > 0 ? 'crit' : undefined}
              onClick={() => openList({ view: 'unregistered' })}
            />
            <Kpi label={t('ads.kpiSensitive')} value={sensitive} sub={t('ads.kpiSensitiveSub')} onClick={() => openList({ view: 'sensitive' })} />
          </div>

          <div className="grid-2">
            <Block title={t('ads.byChannel')} subtitle={t('ads.byChannelSub')}>
              {Object.keys(stats.byPlatform).length === 0 ? (
                <p className="empty-note">{t('ads.noAdvertiserYet')}</p>
              ) : (
                <BarList
                  rows={Object.entries(stats.byPlatform)
                    .sort((a, b) => b[1] - a[1])
                    .map(([p, n]) => ({ key: p, label: platformLabel(t, p), count: n }))}
                  onSelect={(p) => openList({ platform: p })}
                />
              )}
            </Block>
            <Block title={t('ads.topSectors')}>
              {Object.keys(stats.byCategory).length === 0 ? (
                <p className="empty-note">{t('ads.noSectorYet')}</p>
              ) : (
                <BarList
                  rows={Object.entries(stats.byCategory)
                    // Les bannières rangent le domaine du site dans ce champ : on ne garde que les vrais secteurs.
                    .filter(([c]) => QUERY_CATEGORIES.includes(c))
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 6)
                    .map(([c, n]) => ({ key: c, label: categoryLabel(t, c), count: n }))}
                  onSelect={(c) => openList({ category: c })}
                />
              )}
            </Block>
          </div>

          <Block
            title={t('ads.toReviewBlock')}
            subtitle={t('ads.toReviewBlockSub')}
            action={
              fresh && fresh.total > 0 ? (
                <button type="button" className="block-link" onClick={() => openList({ view: 'review' })}>
                  {t('ads.seeAllAdvertisers', { n: fresh.total })}
                </button>
              ) : undefined
            }
          >
            {!fresh || fresh.rows.length === 0 ? (
              <p className="empty-note">{t('ads.noneToReview')}</p>
            ) : (
              <ul className="row-list">
                {fresh.rows.map((r) => (
                  <li key={r.id}>
                    <button type="button" className="row-link" onClick={() => openList({ view: 'review', id: r.id })}>
                      <span className="row-main">
                        <strong>{r.advertiserName}</strong>
                        <span>{r.domain || hostname(r.sourceUrl) || '—'}</span>
                      </span>
                      <span className="row-meta">{platformLabel(t, r.platform)}</span>
                      <span className="row-meta ads-hide-narrow">{categoryLabel(t, r.detectedCategory)}</span>
                      <FiscalPill status={r.fiscalStatus} />
                      <span className="row-open">{t('ads.check')}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Block>
        </>
      )}
    </>
  )
}
